import { Link, useSearchParams } from 'react-router';
import { ArrowRight, Briefcase, Building2, ShieldCheck, Sparkles, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { EnterpriseProofPack } from '../components/enterprise/EnterpriseProofPack';
import { EnterpriseSalesNav } from '../components/enterprise/EnterpriseSalesNav';
import { useSEO } from '../lib/seo';
import { EnterpriseOrgSwitcher } from '../components/enterprise/EnterpriseOrgSwitcher';
import { CURRENT_ENTERPRISE_PHASE, CURRENT_ENTERPRISE_WAVE, ENTERPRISE_ROADMAP } from '../lib/enterprise-roadmap';

const pillars = [
  {
    icon: Building2,
    title: 'Reduce avoidable people-risk before it hits productivity',
    body: 'TENSION gives employers and benefits teams earlier visibility into relationship strain signals so support can happen before burnout, absenteeism, and disengagement escalate.',
  },
  {
    icon: ShieldCheck,
    title: 'Security, privacy, and procurement readiness built in',
    body: 'Role-scoped access, retention controls, and audit-ready operating standards are designed to help enterprise buyers clear security and legal review faster.',
  },
  {
    icon: Users,
    title: 'Actionable aggregate insights without exposing private content',
    body: 'Leaders get cohort-level trend intelligence and risk movement, while individual private conversations remain protected by consent and boundary controls.',
  },
  {
    icon: Briefcase,
    title: 'Deployment model that scales from pilot to enterprise rollout',
    body: 'Launch quickly with a pilot cohort, then expand through multi-tenant controls, SSO pathways, domain governance, and enterprise admin operations.',
  },
];

export default function Enterprise() {
  const [searchParams, setSearchParams] = useSearchParams();
  const boardDeckMode = searchParams.get('view') === 'deck';

  const toggleBoardDeckMode = () => {
    const next = new URLSearchParams(searchParams);
    if (boardDeckMode) {
      next.delete('view');
    } else {
      next.set('view', 'deck');
    }
    setSearchParams(next, { replace: true });
  };

  useSEO({
    title: 'TENSION Enterprise | Relationship Wellness for Teams',
    description:
      'Explore TENSION Enterprise: a separate B2B path for relationship wellness, future org analytics, and enterprise-ready expansion.',
    keywords: [
      'relationship wellness for teams',
      'enterprise relationship intelligence',
      'employee relationship wellness',
      'tension enterprise',
      'contact sales relationship app',
    ],
    canonical: '/enterprise',
    ogType: 'website',
    twitterCard: 'summary_large_image',
  });

  const roadmapItems = ENTERPRISE_ROADMAP.map((item) => ({
    title: item.publicTitle,
    value: item.publicValue,
  }));

  return (
    <div className="enterprise-public-shell min-h-screen bg-[radial-gradient(circle_at_top,rgba(56,189,248,0.2),transparent_28%),radial-gradient(circle_at_85%_0%,rgba(168,85,247,0.18),transparent_32%),linear-gradient(180deg,#020617_0%,#0a1020_48%,#020617_100%)]">
      <EnterpriseSalesNav variant="dark" />
      <div className="mx-auto max-w-6xl space-y-8 px-4 py-10 md:py-14">
        <header className="overflow-hidden rounded-2xl border border-slate-600/80 bg-slate-950/82 p-6 shadow-[0_28px_90px_-36px_rgba(59,130,246,0.72)] backdrop-blur-xl md:p-10">
          <div className="grid gap-8 md:grid-cols-[1.1fr_0.9fr] md:items-start">
            <div>
              <img
                src="/branding/tension-enterprise-logo.svg"
                alt="TENSION Enterprise"
                className="mb-5 h-auto w-full max-w-md rounded-xl border border-slate-700/90 bg-slate-950/80 p-1"
              />
              <p className="inline-flex rounded-full border border-blue-400/30 bg-blue-500/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-blue-200">
                Enterprise operating system
              </p>
              <p className="mt-3 inline-flex rounded-full border border-emerald-400/35 bg-emerald-500/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-200">
                Current roadmap wave: Wave {CURRENT_ENTERPRISE_WAVE} - Phase {CURRENT_ENTERPRISE_PHASE.phaseNumber} ({CURRENT_ENTERPRISE_PHASE.title})
              </p>
              <h1 className="mt-4 text-3xl font-black tracking-tight text-white md:text-5xl md:leading-tight">
                Turn relationship stress into measurable retention and performance outcomes
              </h1>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-200 md:text-lg">
                TENSION Enterprise gives HR and leadership teams early, privacy-preserving intelligence on relationship strain
                patterns so they can intervene earlier, protect productivity, and improve workforce stability.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100" onClick={toggleBoardDeckMode}>
                  {boardDeckMode ? 'Exit board deck mode' : 'Board deck mode'}
                </Button>
                {boardDeckMode ? (
                  <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100" onClick={() => window.print()}>
                    Print / Save PDF
                  </Button>
                ) : null}
                <Link to="/business-inquiries?inquiryType=enterprise">
                  <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 px-6 text-white hover:opacity-95">
                    Contact sales <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/enterprise/solutions">
                  <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100">
                    View use-cases
                  </Button>
                </Link>
                <Link to="/enterprise/how-it-works">
                  <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100">
                    How it works
                  </Button>
                </Link>
              </div>
              <EnterpriseOrgSwitcher className="mt-6 max-w-sm" />
            </div>

            <div className="grid gap-3">
              <Card className="rounded-xl border border-slate-600/80 bg-slate-900/82 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Executive signal panel</p>
                <p className="mt-2 text-2xl font-black text-white">Cohort risk movement</p>
                <p className="mt-1 text-sm text-slate-300">Identify pressure trends before they become attrition or performance drag.</p>
              </Card>
              <Card className="rounded-xl border border-slate-600/80 bg-slate-900/82 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Intervention velocity</p>
                <p className="mt-2 text-2xl font-black text-white">Faster action windows</p>
                <p className="mt-1 text-sm text-slate-300">Trigger support motions while outcomes are still recoverable.</p>
              </Card>
              <Card className="rounded-xl border border-slate-600/80 bg-slate-900/82 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Governance readiness</p>
                <p className="mt-2 text-2xl font-black text-white">Enterprise controls</p>
                <p className="mt-1 text-sm text-slate-300">Identity, seat governance, and policy boundaries for secure scale.</p>
              </Card>
            </div>
          </div>
        </header>

        <section className="grid gap-3 md:grid-cols-3">
          <Card className="rounded-xl border border-slate-700 bg-slate-900/80 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Operational focus</p>
            <p className="mt-1 text-sm font-semibold text-slate-100">Retention, stability, and support utilization intelligence</p>
          </Card>
          <Card className="rounded-xl border border-slate-700 bg-slate-900/80 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Buyer fit</p>
            <p className="mt-1 text-sm font-semibold text-slate-100">HR leadership, EAP partners, and enterprise wellbeing teams</p>
          </Card>
          <Card className="rounded-xl border border-slate-700 bg-slate-900/80 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Deployment path</p>
            <p className="mt-1 text-sm font-semibold text-slate-100">Pilot cohort to controlled multi-org enterprise expansion</p>
          </Card>
        </section>

        {boardDeckMode ? (
          <section className="space-y-5">
            <Card className="enterprise-surface-card enterprise-deck-slide p-7">
              <p className="enterprise-deck-kicker">Slide 01 - Category thesis</p>
              <h2 className="mt-2 text-2xl font-black text-slate-100 md:text-3xl">
                TENSION is positioning a first-of-kind category: workforce relationship-risk intelligence
              </h2>
              <p className="mt-3 max-w-4xl text-base text-slate-300">
                Most enterprise systems monitor productivity after impact. TENSION is designed to surface relationship-driven
                strain signals earlier so organizations can intervene before retention, attendance, and performance degrade.
              </p>
            </Card>

            <Card className="enterprise-surface-card enterprise-deck-slide p-7">
              <p className="enterprise-deck-kicker">Slide 02 - Market proof</p>
              <div className="mt-2 grid gap-3 md:grid-cols-2">
                <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-4">
                  <p className="text-3xl font-black text-slate-100">$8.8T</p>
                  <p className="mt-1 text-sm text-slate-300">Global lost productivity from low engagement.</p>
                  <a href="https://www.gallup.com/workplace/393497/employee-engagement-drives-growth.aspx" target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs font-semibold text-blue-300 hover:underline">Gallup source</a>
                </div>
                <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-4">
                  <p className="text-3xl font-black text-slate-100">$1T</p>
                  <p className="mt-1 text-sm text-slate-300">Annual productivity loss from depression/anxiety.</p>
                  <a href="https://www.who.int/news-room/fact-sheets/detail/mental-health-at-work" target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs font-semibold text-blue-300 hover:underline">WHO source</a>
                </div>
                <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-4">
                  <p className="text-3xl font-black text-slate-100">12B days</p>
                  <p className="mt-1 text-sm text-slate-300">Working days lost annually to depression/anxiety.</p>
                  <a href="https://www.who.int/news-room/fact-sheets/detail/mental-health-at-work" target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs font-semibold text-blue-300 hover:underline">WHO source</a>
                </div>
                <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-4">
                  <p className="text-3xl font-black text-slate-100">15 cigarettes/day</p>
                  <p className="mt-1 text-sm text-slate-300">Isolation mortality comparison benchmark.</p>
                  <a href="https://www.hhs.gov/about/news/2023/05/03/new-surgeon-general-advisory-raises-alarm-about-devastating-impact-epidemic-loneliness-isolation-united-states.html" target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs font-semibold text-blue-300 hover:underline">U.S. HHS source</a>
                </div>
              </div>
            </Card>

            <Card className="enterprise-surface-card enterprise-deck-slide p-7">
              <p className="enterprise-deck-kicker">Slide 03 - Solution architecture</p>
              <div className="mt-2 grid gap-3 md:grid-cols-2">
                {pillars.map(({ title, body }) => (
                  <div key={title} className="rounded-xl border border-slate-700 bg-slate-900/70 p-4">
                    <p className="text-sm font-semibold text-slate-100">{title}</p>
                    <p className="mt-1 text-sm text-slate-300">{body}</p>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="enterprise-surface-card enterprise-deck-slide p-7">
              <p className="enterprise-deck-kicker">Slide 04 - Implementation roadmap</p>
              <div className="mt-2 grid gap-3 md:grid-cols-2">
                {roadmapItems.map((item) => (
                  <div key={item.title} className="rounded-xl border border-slate-700 bg-slate-900/70 p-4">
                    <p className="text-sm font-semibold text-slate-100">{item.title}</p>
                    <p className="mt-1 text-sm text-slate-300">{item.value}</p>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="enterprise-surface-card enterprise-deck-slide p-7">
              <p className="enterprise-deck-kicker">Slide 05 - Commercial path</p>
              <p className="mt-2 max-w-4xl text-base text-slate-300">
                Land with pilot economics and clear executive scorecards, expand through multi-tenant controls and identity,
                then scale into partner/API channels. This sequencing is designed to shorten procurement cycles and increase
                confidence in enterprise-wide rollout decisions.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/business-inquiries?inquiryType=enterprise">
                  <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 px-6 text-white">Book executive briefing</Button>
                </Link>
              </div>
            </Card>
          </section>
        ) : (
          <>
            <section className="grid gap-4 md:grid-cols-2">
              {pillars.map(({ icon: Icon, title, body }) => (
                <Card key={title} className="rounded-2xl border border-slate-700 bg-slate-900/85 p-6 shadow-md">
                  <div className="inline-flex rounded-xl border border-slate-600 bg-slate-800 p-2">
                    <Icon className="h-8 w-8 text-blue-300" aria-hidden />
                  </div>
                  <h2 className="mt-4 text-lg font-semibold text-slate-50">{title}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">{body}</p>
                </Card>
              ))}
            </section>

            <section className="rounded-2xl border border-slate-700 bg-slate-900/85 p-6 md:p-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-50 md:text-2xl">
                <Sparkles className="h-5 w-5 text-blue-300" aria-hidden />
                Current phase: Wave {CURRENT_ENTERPRISE_WAVE} - Phase {CURRENT_ENTERPRISE_PHASE.phaseNumber}
              </h2>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-300 md:text-base">
                We are currently executing <span className="font-semibold text-slate-100">{CURRENT_ENTERPRISE_PHASE.title}</span>. The focus is
                hardening data integrity, tenant governance, and operating controls so later waves can scale cleanly with executive confidence.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/business-inquiries?inquiryType=enterprise">
                  <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100">
                    Talk with enterprise team
                  </Button>
                </Link>
                <Link to="/enterprise/solutions">
                  <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100">
                    Solutions by buyer
                  </Button>
                </Link>
                <Link to="/enterprise/security">
                  <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100">
                    Security posture
                  </Button>
                </Link>
                <Link to="/enterprise/pilot">
                  <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100">
                    Pilot framework
                  </Button>
                </Link>
                <Link to="/enterprise/platform">
                  <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100">
                    Platform + API
                  </Button>
                </Link>
                <Link to="/enterprise/identity">
                  <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100">
                    Identity
                  </Button>
                </Link>
                <Link to="/enterprise/whitelabel">
                  <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100">
                    White-label
                  </Button>
                </Link>
                <Link to="/enterprise/expansion">
                  <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100">
                    Expansion programs
                  </Button>
                </Link>
                <Link to="/enterprise/live-demo">
                  <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100">
                    Live pitch demo
                  </Button>
                </Link>
              </div>
              <div className="mt-6 grid gap-3 md:grid-cols-2">
                {roadmapItems.map((item) => (
                  <Card key={item.title} className="rounded-xl border border-slate-700 bg-slate-900 p-4">
                    <p className="text-sm font-semibold text-slate-50">{item.title}</p>
                    <p className="mt-1 text-sm text-slate-300">{item.value}</p>
                  </Card>
                ))}
              </div>
            </section>

            <EnterpriseProofPack />
          </>
        )}
      </div>
    </div>
  );
}
